import React, { useEffect, useMemo, useState } from "react";
import { EQUIPMENT_TYPES } from "../constants/equipmentTypes";
import { useNavigate } from "react-router-dom";
import { apiGetBroker, apiPostBroker } from "../api";
import { useAuth } from "../components/AuthContext";
import RateCalculator from "../components/RateCalculator";

function section(title, children) {
  return (
    <div style={{ marginTop: 18, padding: 16, border: "1px solid #e5e5e5", borderRadius: 12, background: "white" }}>
      <div style={{ fontWeight: 900, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

export default function BrokerCreateLoad() {
  const nav = useNavigate();
  const { adminEmail } = useAuth();

  const [shippers, setShippers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  // Shipper selection + quick-create
  const [shipperCompanyId, setShipperCompanyId] = useState("");
  const [showNewShipper, setShowNewShipper] = useState(false);
  const [newShipper, setNewShipper] = useState({
    name: "",
    email: "",
    phone: "",
    address1: "",
    city: "",
    state: "",
    postal: "",
    country: "US",
    customerCode: "",
    parentCompanyId: "",
    billingCompanyId: "",
  });

  // Load fields
  const [referenceNumber, setReferenceNumber] = useState("");
  const [originAddress, setOriginAddress] = useState("");
  const [destAddress, setDestAddress] = useState("");
  const [pickupStart, setPickupStart] = useState("");
  const [deliveryStart, setDeliveryStart] = useState("");
  const [equipmentType, setEquipmentType] = useState("FLATBED");
  const [rateAmountCents, setRateAmountCents] = useState("120000");
  const [weightLbs, setWeightLbs] = useState("");
  const [commodity, setCommodity] = useState("");
  
  // Dimensional fields (oversize/overweight)
  const [heightInches, setHeightInches] = useState("");
  const [widthInches, setWidthInches] = useState("");
  const [lengthInches, setLengthInches] = useState("");
  const [showDimensions, setShowDimensions] = useState(false);
  const [permitCheck, setPermitCheck] = useState(null);

  // Wellsite destination
  const [isWellsiteDestination, setIsWellsiteDestination] = useState(false);
  const [wellsites, setWellsites] = useState([]);
  const [wellsiteSearch, setWellsiteSearch] = useState("");
  const [selectedWellsite, setSelectedWellsite] = useState(null);

  
  // Rate calculator
  const [showRateCalc, setShowRateCalc] = useState(false);

  // Instant Booking (NEW)
  const [instantBookEnabled, setInstantBookEnabled] = useState(false);
  const [instantBookMinRating, setInstantBookMinRating] = useState("0");
  // Factoring Option
  const [factoringOption, setFactoringOption] = useState("NONE");

  const shipperOptions = useMemo(() => {
    return (shippers || []).map((s) => ({ id: s.id, name: s.name }));
  }, [shippers]);

  // Load wellsites for oilfield destinations
  useEffect(() => {
    async function loadWellsites() {
      try {
        const response = await fetch("/api/oilfield/wellsites", {
          headers: { "x-admin-email": adminEmail }
        });
        if (response.ok) {
          const data = await response.json();
          setWellsites(data || []);
        }
      } catch (err) {
        console.error("Failed to load wellsites:", err);
      }
    }
    loadWellsites();
  }, [adminEmail]);

  // Filter wellsites based on search input
  const filteredWellsites = useMemo(() => {
    if (!wellsiteSearch.trim()) return wellsites.slice(0, 10);
    const search = wellsiteSearch.toLowerCase();
    return wellsites.filter(w => 
      w.name?.toLowerCase().includes(search) ||
      w.wellName?.toLowerCase().includes(search) ||
      w.city?.toLowerCase().includes(search) ||
      w.basin?.toLowerCase().includes(search)
    ).slice(0, 10);
  }, [wellsites, wellsiteSearch]);

  // Handle wellsite selection
  function handleWellsiteSelect(ws) {
    setSelectedWellsite(ws);
    setWellsiteSearch(ws.name);
    const addr = [ws.name, ws.city, ws.state].filter(Boolean).join(", ");
    setDestAddress(addr);
  }

  async function loadShippers() {
    const data = await apiGetBroker("/broker/shippers", { adminEmail });
    setShippers(data.shippers || []);
  }

  useEffect(() => {
    loadShippers().catch((e) => setErr(e.message || String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminEmail]);

  // Check permit requirements when dimensions change
  useEffect(() => {
    async function checkPermits() {
      if (!heightInches && !widthInches && !lengthInches && !weightLbs) {
        setPermitCheck(null);
        return;
      }
      try {
        const response = await fetch("/api/permits/check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            heightInches: heightInches ? Number(heightInches) : 0,
            widthInches: widthInches ? Number(widthInches) : 0,
            lengthInches: lengthInches ? Number(lengthInches) : 0,
            weightLbs: weightLbs ? Number(weightLbs) : 0,
            originState: "",
            destState: ""
          })
        });
        if (response.ok) {
          const data = await response.json();
          setPermitCheck(data);
        }
      } catch (err) {
        console.error("Permit check failed:", err);
      }
    }
    const timer = setTimeout(checkPermits, 500);
    return () => clearTimeout(timer);
  }, [heightInches, widthInches, lengthInches, weightLbs]);

  // Check permit requirements when dimensions change
  useEffect(() => {
    async function checkPermits() {
      if (!heightInches && !widthInches && !lengthInches && !weightLbs) {
        setPermitCheck(null);
        return;
      }
      try {
        const response = await fetch("/api/permits/check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            heightInches: heightInches ? Number(heightInches) : 0,
            widthInches: widthInches ? Number(widthInches) : 0,
            lengthInches: lengthInches ? Number(lengthInches) : 0,
            weightLbs: weightLbs ? Number(weightLbs) : 0,
            originState: "",
            destState: ""
          })
        });
        if (response.ok) {
          const data = await response.json();
          setPermitCheck(data);
        }
      } catch (err) {
        console.error("Permit check failed:", err);
      }
    }
    const timer = setTimeout(checkPermits, 500);
    return () => clearTimeout(timer);
  }, [heightInches, widthInches, lengthInches, weightLbs]);

  async function createShipper() {
    setErr("");
    setOk("");
    const name = String(newShipper.name || "").trim();
    if (!name) return setErr("Shipper name is required.");

    setLoading(true);
    try {
      const payload = {
        name,
        email: newShipper.email || undefined,
        phone: newShipper.phone || undefined,
        address1: newShipper.address1 || undefined,
        city: newShipper.city || undefined,
        state: newShipper.state || undefined,
        postal: newShipper.postal || undefined,
        country: newShipper.country || "US",
        customerCode: newShipper.customerCode || undefined,
        parentCompanyId: newShipper.parentCompanyId || undefined,
        billingCompanyId: newShipper.billingCompanyId || undefined,
      };

      const res = await apiPostBroker("/broker/shippers", payload, { adminEmail });

      const created = res.shipper || res.shipperProfile || res.company || res;
      await loadShippers();

      // Try to select the created shipper by name match (MVP)
      const updated = await apiGetBroker("/broker/shippers", { adminEmail });
      const match = (updated.shippers || []).find((s) => s.name === name);
      if (match) setShipperCompanyId(match.id);

      setOk("Shipper created.");
      setShowNewShipper(false);
      setNewShipper({
        name: "",
        email: "",
        phone: "",
        address1: "",
        city: "",
        state: "",
        postal: "",
        country: "US",
        customerCode: "",
        parentCompanyId: "",
        billingCompanyId: "",
      });
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  function handleRateSelected(rateCents) {
    setRateAmountCents(String(rateCents));
    setShowRateCalc(false);
    setOk(`Rate updated to $${(rateCents / 100).toFixed(2)}`);
  }

  // Helper to extract state from address string
  function extractState(address) {
    if (!address) return '';
    // Try to get last part after comma, then get first 2-letter word
    const parts = address.split(',');
    const lastPart = parts[parts.length - 1]?.trim() || '';
    const stateMatch = lastPart.match(/\b([A-Z]{2})\b/);
    return stateMatch ? stateMatch[1] : '';
  }

  async function createLoad() {
    setErr("");
    setOk("");

    if (!shipperCompanyId) return setErr("Pick a shipper.");
    if (!originAddress.trim()) return setErr("Origin address required.");
    if (!destAddress.trim()) return setErr("Destination address required.");
    if (!pickupStart) return setErr("Pickup date/time required.");
    if (!deliveryStart) return setErr("Delivery date/time required.");

    const rate = Number(rateAmountCents);
    if (!Number.isFinite(rate) || rate <= 0) return setErr("rateAmountCents must be a positive number.");

    setLoading(true);
    try {
      const body = {
        shipperCompanyId,
        referenceNumber: referenceNumber || undefined,
        originAddress,
        destAddress,
        pickupStart,
        deliveryStart,
        equipmentType,
        rateAmountCents: rate,
        weightLbs: weightLbs ? Number(weightLbs) : undefined,
        commodity: commodity || undefined,
        // Dimensional fields
        heightInches: heightInches ? Number(heightInches) : undefined,
        widthInches: widthInches ? Number(widthInches) : undefined,
        lengthInches: lengthInches ? Number(lengthInches) : undefined,
        isOversize: permitCheck?.isOversize || false,
        isOverweight: permitCheck?.isOverweight || false,
        permitRequired: permitCheck?.requiresPermit || false,
        escortRequired: permitCheck?.requiresEscort || false,
        // Instant booking fields
        instantBookEnabled,
        instantBookMinRating: instantBookEnabled ? Number(instantBookMinRating) : undefined,
        factoringOption,
      };

      const data = await apiPostBroker("/broker/loads", body, { adminEmail });
      const id = data.load?.id;
      setOk("Load created.");
      if (id) nav(`../loads/${id}`);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => nav("/app/broker/loads")} style={{ padding: "8px 12px" }}>
          ← Back to Loads
        </button>
        <button onClick={createLoad} style={{ padding: "10px 14px", fontWeight: 900 }} disabled={loading}>
          {loading ? "Working..." : "Create Load"}
        </button>
      </div>

      {err ? (
        <div style={{ marginTop: 14, padding: 12, background: "#ffecec", border: "1px solid #ffb3b3", borderRadius: 10 }}>
          {err}
        </div>
      ) : null}
      {ok ? (
        <div style={{ marginTop: 14, padding: 12, background: "#eaffea", border: "1px solid #9be79b", borderRadius: 10 }}>
          {ok}
        </div>
      ) : null}

      {section(
        "Shipper",
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <select value={shipperCompanyId} onChange={(e) => setShipperCompanyId(e.target.value)} style={{ padding: 10, flex: "1 1 420px" }}>
              <option value="">Select a shipper...</option>
              {shipperOptions.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>

            <button onClick={() => setShowNewShipper((v) => !v)} style={{ padding: "10px 14px", fontWeight: 900 }}>
              {showNewShipper ? "Close" : "+ New Shipper"}
            </button>

            <button onClick={loadShippers} style={{ padding: "10px 14px" }}>
              Refresh Shippers
            </button>
          </div>

          {showNewShipper ? (
            <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 12, background: "#fafafa" }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input placeholder="Name*" value={newShipper.name} onChange={(e) => setNewShipper((p) => ({ ...p, name: e.target.value }))} style={{ padding: 10, flex: "1 1 260px" }} />
                <input placeholder="Email" value={newShipper.email} onChange={(e) => setNewShipper((p) => ({ ...p, email: e.target.value }))} style={{ padding: 10, flex: "1 1 260px" }} />
                <input placeholder="Phone" value={newShipper.phone} onChange={(e) => setNewShipper((p) => ({ ...p, phone: e.target.value }))} style={{ padding: 10, flex: "1 1 200px" }} />
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                <input placeholder="Address" value={newShipper.address1} onChange={(e) => setNewShipper((p) => ({ ...p, address1: e.target.value }))} style={{ padding: 10, flex: "2 1 420px" }} />
                <input placeholder="City" value={newShipper.city} onChange={(e) => setNewShipper((p) => ({ ...p, city: e.target.value }))} style={{ padding: 10, flex: "1 1 220px" }} />
                <input placeholder="State" value={newShipper.state} onChange={(e) => setNewShipper((p) => ({ ...p, state: e.target.value }))} style={{ padding: 10, flex: "0 1 120px" }} />
                <input placeholder="Postal" value={newShipper.postal} onChange={(e) => setNewShipper((p) => ({ ...p, postal: e.target.value }))} style={{ padding: 10, flex: "0 1 140px" }} />
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                <input placeholder="Customer Code (optional)" value={newShipper.customerCode} onChange={(e) => setNewShipper((p) => ({ ...p, customerCode: e.target.value }))} style={{ padding: 10, flex: "1 1 220px" }} />
                <select value={newShipper.parentCompanyId} onChange={(e) => setNewShipper((p) => ({ ...p, parentCompanyId: e.target.value }))} style={{ padding: 10, flex: "1 1 260px" }}>
                  <option value="">Parent (optional)</option>
                  {shipperOptions.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <select value={newShipper.billingCompanyId} onChange={(e) => setNewShipper((p) => ({ ...p, billingCompanyId: e.target.value }))} style={{ padding: 10, flex: "1 1 260px" }}>
                  <option value="">Billing (optional)</option>
                  {shipperOptions.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>

                <button onClick={createShipper} style={{ padding: "10px 14px", fontWeight: 900 }} disabled={loading}>
                  {loading ? "Working..." : "Create Shipper"}
                </button>
              </div>

              <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
                Parent/Billing are optional hierarchy fields (MVP).
              </div>
            </div>
          ) : null}
        </div>
      )}

      {section(
        "Load Details",
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="Reference # (optional)" style={{ padding: 10, flex: "1 1 220px" }} />
            <select value={equipmentType} onChange={(e) => setEquipmentType(e.target.value)} style={{ padding: 10, flex: "1 1 220px" }}>
              {EQUIPMENT_TYPES.map(eq => (
                <option key={eq.value} value={eq.value}>{eq.label}</option>
              ))}
            </select>
            
            <div style={{ flex: "1 1 220px", display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", gap: 4 }}>
                <input 
                  value={rateAmountCents} 
                  onChange={(e) => setRateAmountCents(e.target.value)} 
                  placeholder="Rate (cents)" 
                  style={{ padding: 10, flex: 1 }} 
                />
                <button
                  type="button"
                  onClick={() => setShowRateCalc(!showRateCalc)}
                  style={{
                    padding: "8px 12px",
                    background: showRateCalc ? "#6b7280" : "#DC2626",
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {showRateCalc ? "✕" : "💰"}
                </button>
              </div>
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                ${(parseInt(rateAmountCents || 0) / 100).toFixed(2)}
              </div>
            </div>
            
            <input value={weightLbs} onChange={(e) => setWeightLbs(e.target.value)} placeholder="Weight lbs (optional)" style={{ padding: 10, flex: "1 1 220px" }} />
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input value={originAddress} onChange={(e) => setOriginAddress(e.target.value)} placeholder="Origin address" style={{ padding: 10, flex: "1 1 420px" }} />
            {/* Destination - with Wellsite option */}
            <div style={{ flex: "1 1 420px", position: "relative" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 500 }}>Destination</label>
                <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={isWellsiteDestination}
                    onChange={(e) => {
                      setIsWellsiteDestination(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedWellsite(null);
                        setWellsiteSearch("");
                      }
                    }}
                  />
                  🛢️ Wellsite
                </label>
              </div>
              {isWellsiteDestination ? (
                <>
                  <input
                    value={wellsiteSearch}
                    onChange={(e) => {
                      setWellsiteSearch(e.target.value);
                      setSelectedWellsite(null);
                    }}
                    placeholder="Type to search wellsites..."
                    style={{ padding: 10, width: "100%", borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                  {wellsiteSearch && !selectedWellsite && filteredWellsites.length > 0 && (
                    <div style={{
                      position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                      background: "white", border: "1px solid #d1d5db", borderRadius: 6,
                      maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                    }}>
                      {filteredWellsites.map(ws => (
                        <div
                          key={ws.id}
                          onClick={() => handleWellsiteSelect(ws)}
                          style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                        >
                          <div style={{ fontWeight: 600 }}>{ws.name}</div>
                          <div style={{ fontSize: 11, color: "#666" }}>
                            {[ws.city, ws.state, ws.basin].filter(Boolean).join(" • ")}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedWellsite && (
                    <div style={{ marginTop: 6, padding: 8, background: "#ecfdf5", borderRadius: 6, fontSize: 12 }}>
                      ✅ <strong>{selectedWellsite.name}</strong> - {selectedWellsite.city}, {selectedWellsite.state}
                    </div>
                  )}
                </>
              ) : (
                <input
                  value={destAddress}
                  onChange={(e) => setDestAddress(e.target.value)}
                  placeholder="Destination address"
                  style={{ padding: 10, width: "100%", borderRadius: 6, border: "1px solid #d1d5db" }}
                />
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 260px" }}>
              <label style={{ display: "block", marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Pickup Date & Time *</label>
              <input type="datetime-local" value={pickupStart} onChange={(e) => setPickupStart(e.target.value)} style={{ padding: 10, width: "100%", borderRadius: 6, border: "1px solid #d1d5db" }} />
            </div>
            <div style={{ flex: "1 1 260px" }}>
              <label style={{ display: "block", marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Delivery Date & Time *</label>
              <input type="datetime-local" value={deliveryStart} onChange={(e) => setDeliveryStart(e.target.value)} style={{ padding: 10, width: "100%", borderRadius: 6, border: "1px solid #d1d5db" }} />
            </div>
            <input value={commodity} onChange={(e) => setCommodity(e.target.value)} placeholder="Commodity (optional)" style={{ padding: 10, flex: "1 1 260px" }} />
          </div>

          {showRateCalc && (
            <RateCalculator
              initialData={{
                originState: extractState(originAddress),
                destState: extractState(destAddress),
                equipmentType,
                weightLbs,
                commodity,
                pickupStart,
              }}
              onRateSelected={handleRateSelected}
            />
          )}

          
          {/* Dimensional Fields for Oversize/Overweight */}
          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              onClick={() => setShowDimensions(!showDimensions)}
              style={{
                padding: "8px 16px",
                background: showDimensions ? "#dbeafe" : "#f3f4f6",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 500
              }}
            >
              📐 {showDimensions ? "Hide" : "Show"} Dimensions (Oversize\/Overweight)
            </button>
            
            {showDimensions && (
              <div style={{ marginTop: 12, padding: 16, background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                  <div style={{ flex: "1 1 150px" }}>
                    <label style={{ display: "block", marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Height (inches)</label>
                    <input
                      type="number"
                      value={heightInches}
                      onChange={(e) => setHeightInches(e.target.value)}
                      placeholder="e.g., 162"
                      style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
                    />
                    <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>Standard max: 13'6" (162")</div>
                  </div>
                  <div style={{ flex: "1 1 150px" }}>
                    <label style={{ display: "block", marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Width (inches)</label>
                    <input
                      type="number"
                      value={widthInches}
                      onChange={(e) => setWidthInches(e.target.value)}
                      placeholder="e.g., 102"
                      style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
                    />
                    <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>Standard max: 8'6" (102")</div>
                  </div>
                  <div style={{ flex: "1 1 150px" }}>
                    <label style={{ display: "block", marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Length (inches)</label>
                    <input
                      type="number"
                      value={lengthInches}
                      onChange={(e) => setLengthInches(e.target.value)}
                      placeholder="e.g., 636"
                      style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
                    />
                    <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>Standard max: 53' (636")</div>
                  </div>
                </div>
                
                {permitCheck && (
                  <div style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 8,
                    background: permitCheck.requiresPermit ? "#fef3c7" : "#d1fae5",
                    border: permitCheck.requiresPermit ? "1px solid #f59e0b" : "1px solid #10b981"
                  }}>
                    {permitCheck.warnings?.map((w, i) => (
                      <div key={i} style={{ fontWeight: 600, marginBottom: 4 }}>{w}</div>
                    ))}
                    {permitCheck.stateAnalysis?.map((s, i) => (
                      <div key={i} style={{ fontSize: 12, marginTop: 8 }}>
                        <strong>{s.state}:</strong>
                        {s.issues?.map((issue, j) => (
                          <div key={j} style={{ marginLeft: 12, color: "#92400e" }}>• {issue}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ fontSize: 12, color: "#666" }}>
            Note: Distance is computed server-side where available (Mapbox). Load board/carrier view will display it when present.
          </div>
        </div>
      )}

      {section(
        "⚡ Instant Booking Settings",
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 12,
            padding: 12,
            background: instantBookEnabled ? "#f0fdf4" : "#f9fafb",
            border: instantBookEnabled ? "2px solid #10b981" : "1px solid #e5e7eb",
            borderRadius: 8,
          }}>
            <input
              type="checkbox"
              id="instantBook"
              checked={instantBookEnabled}
              onChange={(e) => setInstantBookEnabled(e.target.checked)}
              style={{ width: 20, height: 20, cursor: "pointer" }}
            />
            <label htmlFor="instantBook" style={{ flex: 1, cursor: "pointer", fontWeight: 600 }}>
              ⚡ Enable Instant Booking
            </label>
            {instantBookEnabled && (
              <span style={{ 
                padding: "4px 12px", 
                background: "#10b981", 
                color: "white", 
                borderRadius: 12, 
                fontSize: 12, 
                fontWeight: 700 
              }}>
                ACTIVE
              </span>
            )}
          </div>

          {instantBookEnabled && (
            <div style={{ 
              padding: 16, 
              background: "#fffbeb", 
              border: "1px solid #fbbf24", 
              borderRadius: 8 
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                📋 Instant Book Requirements
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  Minimum Carrier Rating (0-5)
                </label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={instantBookMinRating}
                  onChange={(e) => setInstantBookMinRating(e.target.value)}
                  style={{ 
                    padding: 8, 
                    width: "120px",
                    borderRadius: 4,
                    border: "1px solid #d1d5db"
                  }}
                />
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Only carriers with rating ≥ {instantBookMinRating} can instantly book
                </div>
              </div>

              <div style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", padding: 10, borderRadius: 6 }}>
                <strong>ℹ️ How it works:</strong> Approved carriers matching your requirements can book this load instantly without waiting for your approval. The load will be automatically assigned to them.
              </div>
            </div>
          )}

          {!instantBookEnabled && (
            <div style={{ fontSize: 13, color: "#6b7280", fontStyle: "italic" }}>
              When enabled, approved carriers can book this load instantly without manual approval.
            </div>
          )}
        </div>
      )}
      {section(
        "💵 Quick Pay / Factoring Options",
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
            Control whether carriers can request Quick Pay (factoring) for this load.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {[
              { value: "NONE", label: "No Factoring", desc: "Quick Pay not available", icon: "🚫" },
              { value: "IN_HOUSE", label: "In-House Only", desc: "RoadOptix Quick Pay (up to 50%)", icon: "🏠" },
              { value: "EXTERNAL", label: "External Only", desc: "Third-party factoring (Triumph, etc.)", icon: "🏦" },
              { value: "BOTH", label: "Both Options", desc: "Carrier chooses", icon: "✅" }
            ].map(opt => (
              <label
                key={opt.value}
                style={{
                  flex: "1 1 200px",
                  padding: 16,
                  border: factoringOption === opt.value ? "2px solid #059669" : "2px solid #e5e7eb",
                  borderRadius: 8,
                  background: factoringOption === opt.value ? "#ecfdf5" : "white",
                  cursor: "pointer"
                }}
              >
                <input
                  type="radio"
                  name="factoringOption"
                  value={opt.value}
                  checked={factoringOption === opt.value}
                  onChange={(e) => setFactoringOption(e.target.value)}
                  style={{ marginRight: 8 }}
                />
                <span style={{ fontWeight: 700 }}>{opt.icon} {opt.label}</span>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, marginLeft: 24 }}>
                  {opt.desc}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
